import { t } from '../languages';
import { learningContentFrom } from './learning_content.transformer';

/**
 * @module report_overview.transformer
 */

const buildAttributeValues = (values) => {
  if (!values) return null;
  return values.map((value) => ({
    name: value.attributeName,
    value: value.attributeValue,
    score: value.score,
    differ: value.differ,
  }));
};

/**
 * Map list of reporting items (factors or questions) returned by server to
 * front-end format.
 *
 * @example items (from server)
 * [
 *   {
 *     id: 123,
 *     name: "MyFactor",
 *     isKey: true,
 *     score: 0.80,
 *     sinceLastMeasured: 0.02,
 *     compareToOrgWide: 0.01,
 *     hasLargeIncrease: true,
 *     hasLargeDecrease: false,
 *     isTop25: true,
 *     isBottom25: false,
 *     isAboveOrgWide: true,
 *     isBelowOrgWide: false,
 *     topValuesSinceLastMeasured: [
 *       {
 *         name: 'Department',
 *         value: 'Sales',
 *         differ: 0.0551111
 *       }
 *     ],
 *     topValuesCompareToOrgWide: [
 *       {
 *         name: 'Department',
 *         value: 'Sales',
 *         differ: 0.011211
 *       }
 *     ]
 *   }
 * ]
 */
const itemListFrom = (items, isFactorType) =>
  items.map((item) => ({
    id: Number(item.id),
    isFactorType,
    name: item.name,
    isKey: item.isKey,
    score: item.score,
    libraryActions: item.libraryActions,
    learningContent: item?.learningContent?.map(learningContentFrom),
    sinceLastMeasured: item.sinceLastMeasured || null,
    compareToOrgWide: item.compareToOrgWide || null,
    hasLargeIncrease: item.hasLargeIncrease || false,
    hasLargeDecrease: item.hasLargeDecrease || false,
    isTop25: item.isTop25 || false,
    isBottom25: item.isBottom25 || false,
    isAboveOrgWide: item.isAboveOrgWide || false,
    isBelowOrgWide: item.isBelowOrgWide || false,
    topValuesSinceLastMeasured: buildAttributeValues(item.topValuesSinceLastMeasured),
    topValuesCompareToOrgWide: buildAttributeValues(item.topValuesCompareToOrgWide),
  }));

/**
 * Get if a highlight section enabled or disabled
 * front-end format.
 *
 * @example items (from server)
 * {
 *   scoreDifferEnabled: true,
 *   orgRankEnabled: false,
 *   orgDifferEnabled: true,
 *   lastMeasuredSurvey: '2020 engagement survey',
 *   lastMeasuredSurveyId: 100
 * }
 */
const highlightsFrom = (highlights) => ({
  scoreDifferEnabled: highlights.scoreDifferEnabled || false,
  orgRankEnabled: highlights.orgRankEnabled || false,
  orgDifferEnabled: highlights.orgDifferEnabled || false,
  lastMeasuredSurvey: highlights.lastMeasuredSurvey || '',
  // // Temp param, to be remove after move overview response rate to this endpoint
  lastMeasuredSurveyId: highlights.lastMeasuredSurveyId || null,
});

/**
 * Build permission details to permission group label expected by response rate
 */
const permissionLabelFrom = (permissionDetails) => {
  if (permissionDetails.canAccessAll) return '';
  const values = permissionDetails.isAttributeBased
    ? permissionDetails.attributeBasedValues.map((value) => value.attributeValue)
    : [];
  if (permissionDetails.isHierarchyBased) values.unshift(t('analytics.hierarchy'));
  return values.join(', ');
};

const overviewFrom = (overview) => {
  const transformedFactors = overview?.factors?.map((category) => {
    return { ...category, learningContent: category?.learningContent?.map(learningContentFrom) };
  });

  return {
    ...overview,
    factors: transformedFactors,
  };
};

export { itemListFrom, highlightsFrom, permissionLabelFrom, overviewFrom };
